import { EddConfig } from '@/data/config/eddConfig';
import { STRING_TRUE } from '@/data/constants/catalog';
import { ProductType, ResponseProductAttribute } from '@/data/types/Product';

/**
 * Reads one of the per-SKU channel-exclusivity flags the search index projects
 * (`bopisOnly`).
 *
 * These arrive from the index as strings, so a plain truthiness check would read
 * `"false"` as enabled and suppress the label on every product. Casing is not yet
 * confirmed for either field, hence the normalization.
 */
export const isProductFlagEnabled = (value: boolean | string | undefined): boolean =>
	typeof value === 'string' ? value.trim().toLowerCase() === STRING_TRUE : value === true;

export const SAME_DAY_KEY_PREFIX = 'x_itemsameday.';

/**
 * Story 163024 AC11-AC13: the per-store Same Day flag. A plain boolean read —
 * coverage (radius, zip codes) is already resolved by the source system — and a
 * store missing from the map is simply not eligible, never an error.
 */
export const isSameDayFlagEnabled = (
	product: ProductType | undefined,
	physicalStoreId: string
): boolean =>
	!!product &&
	!!physicalStoreId &&
	isProductFlagEnabled(product[`${SAME_DAY_KEY_PREFIX}${physicalStoreId}`] ?? undefined);

export const DROPSHIP_DISABLED_PRODUCTS_ATTRIBUTE = 'DROPSHIP_DISABLED_PRODUCTS';

/**
 * story 159042 AC4's `deliveryOnly` flag isn't a field the search index sends. It's
 * derived instead from DROPSHIP_DISABLED_PRODUCTS attribute
 */
export const isDeliveryOnlyProduct = (
	attributes: Pick<ResponseProductAttribute, 'identifier'>[] | undefined
): boolean =>
	!!attributes?.some((attribute) => attribute.identifier === DROPSHIP_DISABLED_PRODUCTS_ATTRIBUTE);

export const DELIVERY_ONLY_KEY_PREFIX = 'x_itemdeliveryonly.';

/**
 * Story 163615 AC4: the per-store delivery-only flag. A store missing from the map
 * means not delivery only -- never missing data and never an error.
 */
export const isDeliveryOnlyStoreFlagEnabled = (
	product: ProductType | undefined,
	physicalStoreId: string | undefined
): boolean =>
	!!product &&
	!!physicalStoreId &&
	isProductFlagEnabled(product[`${DELIVERY_ONLY_KEY_PREFIX}${physicalStoreId}`] ?? undefined);

/**
 * Story 163615 AC1-AC3, Step 1 of the pickup resolution: the corporate
 * DROPSHIP_DISABLED_PRODUCTS attribute (every store) OR the selected store's
 * `x_itemdeliveryonly` flag. The store flag only ever restricts -- with the OR, it
 * can never re-enable pickup where the attribute blocks it.
 *
 * Not for combo components: their attribute is checked per component and the store
 * flag lives on the parent kit, which the caller already resolves.
 */
export const isDeliveryOnlyInStore = (
	product: ProductType | undefined,
	physicalStoreId: string | undefined
): boolean =>
	isDeliveryOnlyProduct(product?.attributes) ||
	isDeliveryOnlyStoreFlagEnabled(product, physicalStoreId);

/**
 * PICK UP ONLY -- single resolution point (AC19/AC22). No catalog attribute
 * drives this state today, so it reads only the `EDD_PICKUP_ONLY_ENABLED`
 * storeconf, which applies to every product at once (AC21) -- there is nothing
 * per-product to discriminate by yet. When a catalog attribute is introduced,
 * this is the one place that changes to read it instead of (or alongside) the
 * storeconf; nothing else that calls this function needs to change.
 */
export const isPickupOnlyEnabled = (config: EddConfig): boolean => config.pickupOnlyEnabled;
